import { EventEmitter, inject, Injectable, NgZone } from '@angular/core';
import { DomainRepoGetParamsType } from '@client/app/services/api/domain-repo/domain-repo.service';
import { DomainService } from '@client/app/services/entities/domain/domain.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { LocalStorageService } from '@monsido/angular-shared-components';
import { MON_EVENTS } from '@monsido/ng2/core/constants/mon-events.constant';
import { SessionService } from '@monsido/ng2/core/session/session.service';
import { Domain } from '@monsido/ng2/modules/models/api/domain';
import { DomainGroup } from '@monsido/ng2/modules/models/api/domain-group';
import { Login } from '@monsido/ng2/modules/models/api/interfaces/domain.interface';
import { MonEventService } from '@monsido/ng2/services/mon-event/mon-event.service';
import { ErrorHandler } from '../error-handler/error-handler';
import { CrawlHistoryInfoService } from '../crawl-history-info/crawl-history-info.service';
import { ScanDetailsDialogComponent } from '@client/app/components/dialogs/scan-details/scan-details.component';

// HACK: Why are there CoreDomainService and DomainService
@Injectable({
    providedIn: 'root',
})
export class CoreDomainService {
    updateDomainFailedAttempts = 0;
    getDomainsIntervalId: number | null = null;
    updateDomainIntervalId: number | null = null;

    onDomainChanged = new EventEmitter<void>(false);
    ngZone = inject(NgZone);

    constructor (
        private sessionService: SessionService,
        private domainService: DomainService,
        private errorHandler: ErrorHandler,
        private monEventService: MonEventService,
        private totalDomainsService: TotalDomainsService,
        private localStorageService: LocalStorageService,
        private crawlHistoryInfoService: CrawlHistoryInfoService,
    ) {}

    changeDomain () {
        this.onDomainChanged.emit();
    }

    disposeDomains (): void {
        this.totalDomainsService.resetDomains();
        this.cancelDomainPromise();
    }

    setDomain (domain: Domain | null): void {
        this.sessionService.domain = domain;

        if (domain?.crawl_history?.accessibility_errors_only != null) {
            this.crawlHistoryInfoService.setAccessibilityErrorsOnly(domain.crawl_history.accessibility_errors_only);
        } else {
            this.crawlHistoryInfoService.setAccessibilityErrorsOnly(Boolean(domain?.features.accessibility_errors_only));
        }
        if (this.sessionService.domain !== null && this.sessionService.domainGroup !== null) {
            const domainGroups = this.sessionService.domain?.domain_groups?.map(function (group) {
                return group.id;
            });
            if (domainGroups?.indexOf(this.sessionService.domainGroup.id) === -1) {
                this.removeDomainGroup();
            }
        }
        this.sessionService.domainObs.next(domain);
    }

    setDomainGroup (domainGroup: DomainGroup | null): void {
        this.sessionService.domainGroup = domainGroup;
        this.localStorageService.setItem('domainGroup', JSON.stringify(domainGroup));
        this.sessionService.domainGroupObs.next(domainGroup);
    }

    clearDomain (): void {
        this.sessionService.domain = null;
    }

    removeDomainGroup (): void {
        this.sessionService.domainGroup = null;
        this.localStorageService.setItem('domainGroup', 'null');
        this.sessionService.domainGroupObs.next(null);
    }

    setupDomainGroupAfterReload (): void {
        let domainGroup: DomainGroup | null = null;
        try {
            const domainGroupStr = this.localStorageService.getItem('domainGroup');
            if (domainGroupStr) {
                domainGroup = JSON.parse(domainGroupStr);
            }
        } catch (e) {
            domainGroup = null;
        }
        this.sessionService.domainGroup = domainGroup;
    }

    async getDomains (): Promise<void> {
        const params = { page_size: 100, mini: true };
        const domainCollection = [];

        this.totalDomainsService.requestAllDomains({
            params,
            domainCollection,
            isRecursive: false,
        })
            .finally(() => {
                this.cancelDomainPromise();
                this.getDomainsIntervalId = window.setInterval(() => {
                    this.getDomains();
                }, 900000);
            });
    }

    disposeDomain (): void {
        if (!this.sessionService.domainObs.isStopped) {
            this.sessionService.domainObs.unsubscribe();
        }
    }

    cancelDomainPromise (): void {
        if (this.getDomainsIntervalId !== null) {
            window.clearInterval(this.getDomainsIntervalId);
            this.getDomainsIntervalId = null;
        }
    }

    refreshDomain (): Promise<void> {
        return this.updateDomain(true);
    }

    init (): void {
        this.totalDomainsService.resetDomains();
        this.cancelUpdateDomainPromise();
        this.ngZone.runOutsideAngular(() => {
            this.updateDomainIntervalId = window.setInterval(() => {
                this.updateDomain();
            }, 90000);
        });
    }

    setupScanLogin (loginOptions: Login | null): Login | null {
        let login: Login | null = null;
        if (loginOptions && loginOptions.type !== 'none') {
            const optionsType = loginOptions.type;
            login = {
                type: optionsType,
                verify: loginOptions.verify as NonNullable<Login['verify']>,
            };
            if (login && loginOptions[optionsType]) {
                // assign only when defined to satisfy exactOptionalPropertyTypes
                (login as any)[optionsType] = loginOptions[optionsType];
            }
        }

        return login;
    }

    openScanDetails (): void {
        const params = {
            body: ScanDetailsDialogComponent,
            size: 'sm',
            classes: 'fade-animation middle center',
            data: {
                domainObservable: this.sessionService.domainObs,
            },
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    hasCrawlStatus (): boolean {
        return this.sessionService.domain !== null && this.sessionService.domain.crawl_status !== null;
    }

    getDomainCrawlStatus (): number {
        if (this.sessionService.domain !== null && this.sessionService.domain.crawl_status !== null) {
            return this.sessionService.domain?.crawl_status?.progress || 0;
        } else {
            return 0;
        }
    }

    private async updateDomain (force = false): Promise<void> {
        if (force || this.sessionService.domain !== null) {
            const params: DomainRepoGetParamsType = {};
            const groupId = this.sessionService.domainGroup ? this.sessionService.domainGroup.id : null;
            if (groupId) {
                params.group_id = groupId;
            }
            if (!this.sessionService.domain) {
                return;
            }
            return this.domainService.get(this.sessionService.domain.id, params).then(
                (domain) => {
                    this.updateDomainFailedAttempts = 0;
                    // Check if domain changed after fetching details
                    if (this.sessionService.domain !== null && domain.id === this.sessionService.domain.id) {
                        this.setDomain(domain);
                    }
                },
                (error) => {
                    this.updateDomainFailedAttempts++;
                    if (this.updateDomainFailedAttempts < 3) {
                        this.updateDomain();
                    } else {
                        this.errorHandler.noopError(error, 'warning');
                    }
                },
            );
        }
    }

    private cancelUpdateDomainPromise (): void {
        if (this.updateDomainIntervalId !== null) {
            window.clearInterval(this.updateDomainIntervalId);
            this.updateDomainIntervalId = null;
        }
    }

}
